import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { concatMap, map, of, switchMap, tap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';

import { SupplierService } from '../services/supplier.service';
import { SupplierBlendService } from '../services/supplier-blend.service';
import { SupplierGroupTemplateService } from '../services/supplier-group-template.service';
import { SupplierActions } from './supplier.actions';
import { selectBlends } from './supplier.selectors';

@Injectable()
export class SupplierEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _service = inject(SupplierService);
  private readonly _blendService = inject(SupplierBlendService);
  private readonly _groupTemplateService = inject(SupplierGroupTemplateService);

  private readonly _toastService = inject(ToastService);

  public readonly loadSuppliers$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(SupplierActions.loadSuppliers),
      switchMap(({ buyerId }) =>
        this._service.getAll(buyerId).pipe(
          map((suppliers) =>
            SupplierActions.loadSuppliersSuccess({ suppliers }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SupplierActions.loadSuppliersFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage: 'Failed to load suppliers. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly saveSupplierAccess$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(SupplierActions.saveSupplierAccess),
      switchMap(({ buyerId, suppliers }) =>
        this._service.saveAccess({ buyerId, suppliers }).pipe(
          tap(() =>
            this._toastService.success(
              'Success! Supplier status has been saved',
            ),
          ),
          map(() => SupplierActions.saveSupplierAccessSuccess()),
          this.psCatchError(
            (error) =>
              of(
                SupplierActions.saveSupplierAccessFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to save supplier access. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly loadSupplierBlends$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(SupplierActions.loadSupplierBlends),
      switchMap(() =>
        this._blendService.getAll().pipe(
          map((blends) =>
            SupplierActions.loadSupplierBlendsSuccess({ blends }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SupplierActions.loadSupplierBlendsFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to load supplier blends. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly deleteSupplierBlend$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(SupplierActions.deleteSupplierBlend),
      concatMap(({ id }) =>
        this._blendService.delete(id).pipe(
          tap(() =>
            this._toastService.success('Blend deleted successfully.', {
              keepOpen: false,
            }),
          ),
          map(() => SupplierActions.deleteSupplierBlendSuccess({ id })),
          this.psCatchError(
            (error) =>
              of(
                SupplierActions.deleteSupplierBlendFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to delete supplier blend. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly setSupplierBlendDefault$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(SupplierActions.setSupplierBlendDefault),
      concatMap(({ id, isDefault }) =>
        this._blendService.setDefault(id, isDefault).pipe(
          map(() =>
            SupplierActions.setSupplierBlendDefaultSuccess({ id, isDefault }),
          ),
          this.psCatchError((error) => {
            this._toastService.error(error.message, { keepOpen: false });
            return of(
              SupplierActions.setSupplierBlendDefaultFailure({
                error: error.message,
              }),
            );
          }),
        ),
      ),
    );
  });

  public readonly recoverSetSupplierBlendDefaultFailure$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.setSupplierBlendDefaultFailure),
      map(() => SupplierActions.loadSupplierBlends()),
    ),
  );

  public readonly createSupplierBlend$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(SupplierActions.createSupplierBlend),
      concatMap(({ data }) =>
        this._blendService.create(data).pipe(
          tap(() =>
            this._toastService.success('Supplier blend created successfully.', {
              keepOpen: false,
            }),
          ),
          map((blend) => SupplierActions.createSupplierBlendSuccess({ blend })),
          this.psCatchError(
            (error) =>
              of(
                SupplierActions.createSupplierBlendFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to create supplier blend. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly updateSupplierBlend$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(SupplierActions.updateSupplierBlend),
      concatMap(({ id, data, keepFormOpen }) =>
        this._blendService.update(id, data).pipe(
          tap(() => {
            if (!keepFormOpen) {
              this._toastService.success(
                'Supplier blend updated successfully.',
                { keepOpen: false },
              );
            }
          }),
          map((blend) =>
            SupplierActions.updateSupplierBlendSuccess({ blend, keepFormOpen }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SupplierActions.updateSupplierBlendFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to update supplier blend. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly lockAllSupplierBlends$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(SupplierActions.lockAllSupplierBlends),
      concatLatestFrom(() => this._store.select(selectBlends)),
      concatMap(([{ isLocked }, blends]) =>
        this._blendService
          .lockAll(
            blends.map((blend) => blend.id),
            isLocked,
          )
          .pipe(
            map(() =>
              SupplierActions.lockAllSupplierBlendsSuccess({ isLocked }),
            ),
            this.psCatchError(
              (error) =>
                of(
                  SupplierActions.lockAllSupplierBlendsFailure({
                    error: error.message,
                  }),
                ),
              {
                notify: true,
                customMessage:
                  'Failed to lock supplier blends. Please try again.',
              },
            ),
          ),
      ),
    );
  });

  public readonly loadGroupTemplates$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(SupplierActions.loadGroupTemplates),
      switchMap(() =>
        this._groupTemplateService.getAll().pipe(
          map((templates) =>
            SupplierActions.loadGroupTemplatesSuccess({ templates }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SupplierActions.loadGroupTemplatesFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to load supplier group templates. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly saveGroupAsTemplate$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(SupplierActions.saveGroupAsTemplate),
      concatMap(({ data }) =>
        this._groupTemplateService.save(data).pipe(
          tap(() =>
            this._toastService.success(
              'Supplier group template saved successfully.',
              { keepOpen: false },
            ),
          ),
          map(() => SupplierActions.saveGroupAsTemplateSuccess()),
          this.psCatchError(
            (error) =>
              of(
                SupplierActions.saveGroupAsTemplateFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Unable to save this group template. Please refresh and try again.',
            },
          ),
        ),
      ),
    );
  });
}
